import React, { useMemo } from 'react'
import { useQuery } from '@apollo/client'
import { Booklet } from '../../../../types/generated.types'
import ResellerStatsTable, { ResellerStats } from './ResellerStatsTable'
import { getBookletResellerStatsSimpleQuery } from '../../../../queries/booklet'
import { ResellerType } from '../../../super-admin/AdminResellerStats';

interface WebshopStatsProps {
  associationId: string;
  selectedBooklet?: Booklet;
  mainAssociationName?: string;
}

const WebshopStats: React.FC<WebshopStatsProps> = ({
  associationId,
  selectedBooklet,
  mainAssociationName
}) => {
  const { data, loading, error } = useQuery(getBookletResellerStatsSimpleQuery, {
    variables: {
      bookletId: selectedBooklet?._id,
      associationId: associationId,
      fromDate: null,
      toDate: null
    },
    skip: !selectedBooklet?._id || !associationId
  });

  // Dela upp stats i två arrays - huvudföreningen och alla andra (hierarkiskt sorterat)
  const { mainAssociationStats, otherStats } = useMemo(() => {
    const allAssociations = data?.stats?.associations || [];
    const teams = data?.stats?.teams || [];
    const teamMembers = data?.stats?.teamMembers || [];

    // Huvudföreningen är alltid första
    const mainAssociation = allAssociations[0];
    const otherAssociations = allAssociations.slice(1);

    // Bygg hierarkisk struktur: Förening > Lag > Lagmedlem
    const result: ResellerStats[] = [];
    const associationIds = new Set<string>();
    const addedTeamIds = new Set<string>();
    const addedMemberIds = new Set<string>();

    // Organisera teams och members per parent
    const teamsByAssoc = new Map<string, typeof teams>();
    teams.forEach(t => {
      const assocId = String(t.team.associationId);
      if (!teamsByAssoc.has(assocId)) teamsByAssoc.set(assocId, []);
      teamsByAssoc.get(assocId)!.push(t);
    });
    const membersByTeam = new Map<string, typeof teamMembers>();
    teamMembers.forEach(m => {
      const teamId = String(m.teamMember.teamId);
      if (!membersByTeam.has(teamId)) membersByTeam.set(teamId, []);
      membersByTeam.get(teamId)!.push(m);
    });

    // Lägg först till huvudföreningens teams och teamMembers (men inte huvudföreningen själv)
    if (mainAssociation) {
      const mainAssocId = String(mainAssociation.association._id);
      teamsByAssoc.get(mainAssocId)?.forEach(team => {
        const teamId = String(team.team._id);
        result.push({
          resellerId: teamId,
          resellerType: ResellerType.Team,
          name: team.displayName,
          sold: team.sold,
          supportSold: team.supportSold,
          afEarnings: team.afEarnings,
          earnings: team.earnings,
          supportEarnings: team.supportEarnings
        });
        addedTeamIds.add(teamId);

        // Lägg till members
        membersByTeam.get(teamId)?.forEach(member => {
          const memberId = String(member.teamMember._id);
          result.push({
            resellerId: memberId,
            resellerType: ResellerType.TeamMember,
            name: member.displayName,
            sold: member.sold,
            supportSold: member.supportSold,
            afEarnings: member.afEarnings,
            earnings: member.earnings,
            supportEarnings: member.supportEarnings
          });
          addedMemberIds.add(memberId);
        });
      });
    }

    // Gå igenom andra associations och lägg till deras hierarki
    otherAssociations.forEach(assoc => {
      const assocId = String(assoc.association._id);
      result.push({
        resellerId: assocId,
        resellerType: ResellerType.Association,
        name: assoc.displayName,
        sold: assoc.sold,
        supportSold: assoc.supportSold,
        afEarnings: assoc.afEarnings,
        earnings: assoc.earnings,
        supportEarnings: assoc.supportEarnings,
        isSupportable: !!assoc.isSupportable
      });
      associationIds.add(assocId);

      // Lägg till teams
      teamsByAssoc.get(String(assoc.association._id))?.forEach(team => {
        const teamId = String(team.team._id);
        result.push({
          resellerId: teamId,
          resellerType: ResellerType.Team,
          name: team.displayName,
          sold: team.sold,
          supportSold: team.supportSold,
          afEarnings: team.afEarnings,
          earnings: team.earnings,
          supportEarnings: team.supportEarnings
        });
        addedTeamIds.add(teamId);

        // Lägg till members
        membersByTeam.get(teamId)?.forEach(member => {
          const memberId = String(member.teamMember._id);
          result.push({
            resellerId: memberId,
            resellerType: ResellerType.TeamMember,
            name: member.displayName,
            sold: member.sold,
            supportSold: member.supportSold,
            afEarnings: member.afEarnings,
            earnings: member.earnings,
            supportEarnings: member.supportEarnings
          });
          addedMemberIds.add(memberId);
        });
      });
    });

    // Edge cases: teams/members utan parent
    teams.forEach(team => {
      if (!addedTeamIds.has(String(team.team._id))) {
        result.push({
          resellerId: team.team._id,
          resellerType: ResellerType.Team,
          name: team.displayName,
          sold: team.sold,
          supportSold: team.supportSold,
          afEarnings: team.afEarnings,
          earnings: team.earnings,
          supportEarnings: team.supportEarnings
        });
        membersByTeam.get(String(team.team._id))?.forEach(m => {
          if (!addedMemberIds.has(String(m.teamMember._id))) {
            result.push({
              resellerId: m.teamMember._id,
              resellerType: ResellerType.TeamMember,
              name: m.displayName,
              sold: m.sold,
              supportSold: m.supportSold,
              afEarnings: m.afEarnings,
              earnings: m.earnings,
              supportEarnings: m.supportEarnings
            });
          }
        });
      }
    });

    // Filtrera bort föreningar med 0 i all statistik
    const filteredOtherStats = result.filter(item => {
      const isZeroRow = (item.sold || 0) === 0 && (item.afEarnings || 0) === 0 && (item.earnings || 0) === 0;
      const isAssociation = associationIds.has(String(item.resellerId));
      const hasTeamsUnder = isAssociation && (teamsByAssoc.get(String(item.resellerId))?.length || 0) > 0;
      const isSupportable = item.isSupportable === true;
      // Dölj endast om:
      // Det är en förening OCH 
      // Alla siffror är 0 OCH 
      // Har minst ett lag under sig OCH 
      // INTE är stödbar
      return !(isAssociation && isZeroRow && hasTeamsUnder && !isSupportable);
    });

    return {
      mainAssociationStats: mainAssociation ? [{
        resellerId: mainAssociation.association._id,
        resellerType: ResellerType.Association,
        name: mainAssociationName || mainAssociation.displayName,
        sold: mainAssociation.sold,
        supportSold: mainAssociation.supportSold,
        afEarnings: mainAssociation.afEarnings,
        earnings: mainAssociation.earnings,
        supportEarnings: mainAssociation.supportEarnings
      }] : [],
      otherStats: filteredOtherStats
    };
  }, [data, mainAssociationName]);

  const totalWebshopEarnings = data?.stats?.totals?.totalEarnings || 0;
  const commissionSettings = data?.stats?.commissionSettings;

  if (loading) {
    return <p>Laddar statistik...</p>;
  }

  if (error) {
    console.error('Stats query error:', error);
    return <p>Fel vid laddning av statistik.</p>;
  }

  return (
    <>
      {/* Tabell 1: Huvudföreningen endast (utan ÅF:s förtjänst) */}
      {mainAssociationStats.length > 0 && (
        <ResellerStatsTable stats={mainAssociationStats} commissionSettings={commissionSettings} isMainAssociation />
      )}

      {/* Tabell 2: Alla andra återförsäljare (med ÅF:s förtjänst) */}
      {otherStats.length > 0 && (
        <>
          <ResellerStatsTable stats={otherStats} commissionSettings={commissionSettings} />
          <div className="table-footer">
            <span>Summa, er förtjänst</span>
            <span></span>
            <span><strong>{totalWebshopEarnings} kr</strong></span>
          </div>
        </>
      )}
    </>
  )
}

export default WebshopStats
